import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import lang from '@/utils/Language';
import classes from './GoogleLocation.module.scss';
import useLocation from '@/hooks/useLocation';
import AutoComplete from './AutoComplete';
import FindLocation from './FindLocation';
import MapLocation from './MapLocation';
import { LocationContext } from '@/context/location.context';

const GoogleLocation = ({
  answers,
  locationObject, // expected to be the standard location object OD stores in localStorage
  saveLocationFn,
  showFindLocation,
  additionalClasses,
  ...props
}) => {
  const autoCompleteElementId = 'google-location-search-autocomplete';
  const [options, setOptions] = useState({ center: {} });
  const { locationContext } = useLocation();
  const showMap = typeof props.showMap == 'undefined' ? true : props.showMap;
  const defaultLat = props.lat ? parseFloat(props.lat) : locationContext.location?.lat || 37.775354; // default to San Francisco
  const defaultLng = props.lng ? parseFloat(props.lng) : locationContext.location?.lng || -122.419101; // default to San Francisco
  const { locationHelpers } = useContext(LocationContext);

  const onAnyKeyExistsAndChanged = (keys, newState, oldState) => {
    for (let i = 0; i < keys.length; i++) {
      if (newState[keys[i]] && newState[keys[i]] !== oldState[keys[i]]) {
        return true;
      }
    }
    return false;
  };

  // update the component state and trigger an onClick if it was passed in on props
  const setState = (state = {}) => {
    setOptions((prevState) => {
      if (props.onClick && onAnyKeyExistsAndChanged(['lat', 'lng', 'zip', 'address'], state, prevState)) {
        props.onClick(props.answerType, state);
      }

      return {
        ...prevState,
        ...state
      };
    });
  };

  useEffect(() => {
    const defaultState = {
      mapApiLoaded: false,
      mapInstance: null,
      mapApi: null,
      geoCoder: null,
      places: [],
      center: {
        lat: parseFloat(defaultLat),
        lng: parseFloat(defaultLng)
      },
      zoom: props.zoom,
      address: '',
      lat: parseFloat(defaultLat),
      lng: parseFloat(defaultLng),
      state: null,
      county: null,
      zip: null
    };
    setState(defaultState);
  }, []);

  useEffect(() => {
    if (props.lat && props.lng) {
      const lat = parseFloat(props.lat);
      const lng = parseFloat(props.lng);
      setOptions((prevState) => {
        return {
          ...prevState,
          ...Object.assign({}, { center: { lat: lat, lng: lng }, lat: lat, lng: lng })
        };
      });
    }
  }, [props.lat, props.lng]);

  const onSelectLocation = ({ lat, lng }) => {
    generateAddress(lat, lng);
  };

  // TODO: hide other result names on click, prevent moving the middle marker
  const onClick = (value) => {
    generateAddress(value.lat, value.lng);
  };

  const apiHasLoaded = (map, maps) => {
    setState({
      mapApiLoaded: true,
      mapInstance: map,
      mapApi: maps
    });
  };

  const addPlace = (place) => {
    setState({
      places: [place],
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng()
    });
    // generateAddress()
  };

  const renderAutoComplete = () => {
    return (
      <AutoComplete
        elementId={autoCompleteElementId}
        style={props.styles?.autoComplete}
        map={options.mapInstance}
        mapApi={options.mapApi}
        addplace={addPlace}
        address={options.address}
        locationObject={locationObject}
        saveLocationFn={saveLocationFn}
      />
    );
  };

  useEffect(() => {
    generateAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.mapApiLoaded, options.places]);

  const generateAddress = (lat, lng) => {
    const { mapApi, mapApiLoaded } = options;

    if (!mapApiLoaded) {
      return console.warn('mapApi is not loaded yet');
    }

    const geocoder = new mapApi.Geocoder();
    const geocode_lat = lat || options.lat;
    const geocode_lng = lng || options.lng;

    geocoder.geocode({ location: { lat: geocode_lat, lng: geocode_lng } }, (results, status) => {
      if (status === 'OK') {
        if (results[0]) {
          let newState = results[0];
          const result = results[0];
          Object.assign(newState, {
            state: locationHelpers.getStateFromPlace(result, false),
            zip: locationHelpers.getZipFromPlace(result),
            county: locationHelpers.getCountyFromPlace(result),
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
            zoom: props.zoom
          });
          setState(newState);
        } else {
          console.warn('No results found');
        }
      } else {
        console.warn('Geocoder failed due to: ' + status);
      }
    });
  };

  return (
    <div className={classes.GoogleLocation}>
      {!props.for && props.label && <label dangerouslySetInnerHTML={{ __html: lang.getLanguageString(props.label) }} />}
      {props.for?.map((option, idx) => {
        return (
          <React.Fragment key={idx}>
            {option.options.indexOf(Object.keys(answers[option.name])[0]) > -1 && (
              <label dangerouslySetInnerHTML={{ __html: lang.getLanguageString(option.label) }} />
            )}
          </React.Fragment>
        );
      })}
      <p className={classes.error}>{lang.getLanguageString(props.error)}</p>
      {options.mapApiLoaded && props.autocomplete === 'top' && <div>{renderAutoComplete()}</div>}
      {showMap && (
        <MapLocation
          styles={props.styles?.mapContainer}
          onMapApiLoaded={({ map, maps }) => apiHasLoaded(map, maps)}
          defaultZoom={options.zoom}
          places={props.places}
          selectedLocation={{ lat: options.lat, lng: options.lng, locationName: options.address }}
          onSelectLocation={onSelectLocation}
        />
      )}
      {options.mapApiLoaded && props.autocomplete === 'bottom' && <div>{renderAutoComplete()}</div>}
      {showFindLocation && <FindLocation setState={setState} saveLocationFn={saveLocationFn} additionalClasses={additionalClasses} />}
      {props.debug && (
        <div className="info-wrapper">
          <div className="map-details">
            Latitude: <span>{options.lat}</span>, Longitude: <span>{options.lng}</span>
          </div>
          <div className="map-details">
            Zoom: <span>{options.zoom}</span>
          </div>
          <div className="map-details">
            Address: <span>{options.address}</span>
          </div>
          <div className="map-details">
            Center:{' '}
            <span>
              {options.center.lat} {options.center.lng}
            </span>
          </div>
          <div className="map-details">
            Api Loaded: <span>{options.mapApiLoaded ? 'YES' : 'NO'}</span>
          </div>
          <div className="map-details">
            Places: <span>{props.places.length}</span>
          </div>
        </div>
      )}
    </div>
  );
};

GoogleLocation.propTypes = {
  onClick: PropTypes.func,
  debug: PropTypes.bool,
  autocomplete: PropTypes.oneOf(['bottom', 'top']),
  lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lng: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  zoom: PropTypes.number,
  places: PropTypes.array,
  answers: PropTypes.object
};

GoogleLocation.defaultProps = {
  debug: false,
  zoom: 9,
  places: [],
  onChange: () => {},
  onClick: (name, value) => {
    console.info({ name, value });
  },
  answers: {}
};

export default GoogleLocation;
